// ============================================================================
// CsvBulkUpload — paste/upload a CSV of events and create them in bulk.
// ============================================================================
// Routed via #admin/events/bulk (legacy #admin-event-bulk auto-redirects).
// UIF only. PapaParse handles parsing in the browser; validation happens
// row-by-row before submission so the admin can fix errors without
// round-tripping the API. Submission calls api.createEvent() per row,
// sequentially, with a per-row status display.
//
// Lookup columns (resolved client-side against existing libraries):
//   tag_names      → tag_ids (case-insensitive)
//   series_label   → series_id (case-insensitive)
//   prereq_slugs   → prereq_event_ids (exact slug match)
//
// Unknown lookups produce a row-level warning but don't block submission —
// the resulting event just won't get that tag/series/prereq.
//
// Round 1-3 column expansion (task #35) adds:
//   slug, series_label, sequence_number, instance_label,
//   prereq_slugs, prereq_text, is_recurring_template, instance_notes,
//   agenda_md, about_md, presenters_md,
//   hero_photo_url, video_embed_url,
//   days_json (per-day override schedule for multi-day events)
//
// Multi-line markdown cells (agenda_md, about_md, presenters_md, description,
// instance_notes) must be quoted in the CSV. PapaParse handles quoted cells
// containing newlines correctly when header:true is set.
//
// Date format: YYYY-MM-DDTHH:MM (e.g. "2026-07-13T08:30"). Treated as
// UTC-ish per the rest of the app's naive-timestamp convention.
// ============================================================================

import { Fragment, useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import {
  ArrowLeft, Upload, Download, Loader2, CheckCircle2, AlertCircle, FileText,
  Pencil, ChevronDown, ChevronUp, ArrowRight, Plus,
} from 'lucide-react';
import {
  api,
  type Audience,
  type EventTag,
  type EventCreateInput,
  type Provision,
  type Series,
  type PublicEvent,
  type EventDay,
  type SharedResource,
} from '../api';
import { useAuth } from '../auth';

const AUDIENCES: Audience[] = ['public', 'amp', 'agc', 'internal'];
const PROVISIONS: Provision[] = ['breakfast', 'lunch', 'dinner', 'snacks'];

// venue_details is NOT a CSV column — it auto-fills from the location library
// when an event is opened in the form. Bulk-uploaded events get null
// venue_details on creation; the admin can edit them individually if needed,
// or — recommended — set up the location in the Locations panel first so
// the library handles the venue text once-and-done across many events.
const TEMPLATE_HEADERS = [
  // Core event content (existing)
  'title',
  'audience',
  'start_date',
  'end_date',
  'location',
  // Task #47: resource_name — case-insensitive lookup against the shared
  // resources library. If matched, the event is created with that
  // resource_id and the backend creates a booking (proposed if the event
  // is a draft, confirmed otherwise).
  'resource_name',
  'max_attendees',
  'description',
  'event_type',
  'cost_text',
  'stipend_text',
  'doors_open_text',
  'materials',
  'tag_names',
  'provisions',
  // Round 1 (slug)
  'slug',
  // Round 2 (modal markdown)
  'agenda_md',
  'about_md',
  'presenters_md',
  // Round 2 (media)
  'hero_photo_url',
  'video_embed_url',
  // Round 2 (series + prereq)
  'series_label',
  'sequence_number',
  'instance_label',
  'prereq_slugs',
  'prereq_text',
  // Round 3 (recurring template)
  'is_recurring_template',
  'instance_notes',
  // Round 2 (multi-day overrides)
  'days_json',
] as const;

type CsvHeader = typeof TEMPLATE_HEADERS[number];

const TEMPLATE_SAMPLE_ROWS: Array<Record<CsvHeader, string>> = [
  {
    title: 'Postharvest Handling Workshop',
    audience: 'public',
    start_date: '2026-08-12T08:30',
    end_date: '2026-08-12T17:00',
    location: 'Up in Farms Training Center, 2093 Lakeland Dr, Jackson, MS 39216',
    resource_name: 'Up in Farms Training Center',
    max_attendees: '16',
    description: 'Hands-on session on cleaning, cooling, and sorting produce after harvest.',
    event_type: 'Workshop',
    cost_text: 'Free',
    stipend_text: '',
    doors_open_text: '8:00am - coffee and sign-in',
    materials: 'Notebook, current recordkeeping samples',
    tag_names: 'Food Safety',
    provisions: 'lunch',
    slug: 'postharvest-handling-20260812',
    agenda_md: '## Morning\n- 8:30 Welcome\n- 9:00 Cooling demo\n\n## Afternoon\n- 1:00 Sorting practice',
    about_md: '',
    presenters_md: '## Kayla McMinn\n*Food Safety Specialist*\n\n![Kayla](/presenters/kayla.jpg)',
    hero_photo_url: '',
    video_embed_url: '',
    series_label: '',
    sequence_number: '',
    instance_label: '',
    prereq_slugs: '',
    prereq_text: '',
    is_recurring_template: '',
    instance_notes: '',
    days_json: '',
  },
  {
    title: 'SmartFarmed Food Safety Workshop',
    audience: 'public',
    start_date: '2026-09-14T08:30',
    end_date: '2026-09-15T17:00',
    location: 'Up in Farms Training Center, 2093 Lakeland Dr, Jackson, MS 39216',
    resource_name: 'Up in Farms Training Center',
    max_attendees: '16',
    description: 'Two-day food safety workshop covering FSMA and GAP basics.',
    event_type: 'Workshop',
    cost_text: 'Free',
    stipend_text: '$100 total stipend for both days',
    doors_open_text: '',
    materials: '',
    tag_names: 'Food Safety, AMP',
    provisions: '',
    slug: 'smartfarmed-food-safety-20260914',
    agenda_md: '',
    about_md: '',
    presenters_md: '',
    hero_photo_url: '',
    video_embed_url: '',
    series_label: '',
    sequence_number: '',
    instance_label: '',
    prereq_slugs: '',
    prereq_text: 'Basic familiarity with farm operations',
    is_recurring_template: '',
    instance_notes: '',
    days_json: '[{"day_index":1,"day_date":"2026-09-14","start_time":"08:30","end_time":"17:00","provisions":["breakfast","lunch"],"agenda_md":"Day 1: FSMA basics"},{"day_index":2,"day_date":"2026-09-15","start_time":"09:00","end_time":"17:00","provisions":["lunch"],"agenda_md":"Day 2: GAP audit prep"}]',
  },
];

// ----- Validation ---------------------------------------------------------

interface ParsedRow {
  rowNumber: number;        // 1-indexed in the source CSV (header is row 1, first data is row 2)
  raw: Record<string, string>;
  payload?: EventCreateInput;
  errors: string[];
  warnings: string[];
}

function trimAll(o: Record<string, any>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(o)) out[k.trim().toLowerCase()] = String(o[k] ?? '').trim();
  return out;
}

function toISOIfPresent(local: string): string | null {
  if (!local) return null;
  const m = local.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const sec = m[3] ?? '00';
  return `${m[1]}T${m[2]}:${sec}Z`;
}

// Loose boolean coercion: true/yes/y/1 → true; everything else → false.
function parseBool(raw: string | undefined): boolean {
  if (!raw) return false;
  const v = raw.trim().toLowerCase();
  return v === 'true' || v === 'yes' || v === 'y' || v === '1';
}

interface LookupTables {
  tagsByName: Map<string, EventTag>;
  seriesByLabel: Map<string, Series>;
  eventBySlug: Map<string, PublicEvent>;
  // Task #47: resource_name → SharedResource lookup (case-insensitive).
  resourceByName: Map<string, SharedResource>;
}

function validateRow(
  rowNumber: number,
  raw: Record<string, string>,
  lookups: LookupTables,
): ParsedRow {
  const errors: string[] = [];
  const warnings: string[] = [];

  const title = raw.title;
  if (!title || title.length < 2) errors.push('title is required (min 2 chars)');

  const startISO = toISOIfPresent(raw.start_date);
  const endISO   = toISOIfPresent(raw.end_date);
  if (!startISO) errors.push('start_date must be YYYY-MM-DDTHH:MM (e.g. 2026-07-13T08:30)');
  if (!endISO)   errors.push('end_date must be YYYY-MM-DDTHH:MM');
  if (startISO && endISO && new Date(endISO) <= new Date(startISO)) {
    errors.push('end_date must be after start_date');
  }

  const audience = (raw.audience || 'public') as Audience;
  if (!AUDIENCES.includes(audience)) {
    errors.push(`audience must be one of ${AUDIENCES.join(', ')}`);
  }

  let maxAttendees: number | null = null;
  if (raw.max_attendees) {
    const n = Number(raw.max_attendees);
    if (!Number.isFinite(n) || n < 1 || n > 500) errors.push('max_attendees must be 1–500');
    else maxAttendees = n;
  }

  // Tag resolution (case-insensitive). Comma-separated.
  const tagIds: number[] = [];
  if (raw.tag_names) {
    for (const name of raw.tag_names.split(',').map((s) => s.trim()).filter(Boolean)) {
      const match = lookups.tagsByName.get(name.toLowerCase());
      if (match) tagIds.push(match.tag_id);
      else warnings.push(`unknown tag "${name}" — will be skipped`);
    }
  }

  // Provisions (comma-separated; values restricted to allowlist).
  const provisions: Provision[] = [];
  if (raw.provisions) {
    for (const p of raw.provisions.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)) {
      if ((PROVISIONS as string[]).includes(p)) provisions.push(p as Provision);
      else warnings.push(`unknown provision "${p}" — must be breakfast/lunch/dinner/snacks`);
    }
  }

  // Series lookup by label (case-insensitive).
  let seriesId: number | null = null;
  if (raw.series_label) {
    const match = lookups.seriesByLabel.get(raw.series_label.toLowerCase());
    if (match) seriesId = match.series_id;
    else warnings.push(`unknown series "${raw.series_label}" — event will be unlinked`);
  }

  // Sequence number.
  let sequenceNumber: number | null = null;
  if (raw.sequence_number) {
    const n = Number(raw.sequence_number);
    if (!Number.isFinite(n) || n < 0 || n > 9999) {
      errors.push('sequence_number must be 0–9999');
    } else {
      sequenceNumber = n;
    }
  }

  // Prereq slugs (comma-separated → event_ids).
  const prereqIds: number[] = [];
  if (raw.prereq_slugs) {
    for (const slug of raw.prereq_slugs.split(',').map((s) => s.trim()).filter(Boolean)) {
      const match = lookups.eventBySlug.get(slug);
      if (match) prereqIds.push(match.event_id);
      else warnings.push(`unknown prereq slug "${slug}" — will be skipped`);
    }
  }

  // Resource (room) lookup by name (case-insensitive).
  let resourceId: number | null = null;
  if (raw.resource_name) {
    const match = lookups.resourceByName.get(raw.resource_name.toLowerCase());
    if (match) resourceId = match.resource_id;
    else warnings.push(`unknown resource "${raw.resource_name}" — event will be created without a room booking`);
  }

  // Slug — server enforces uniqueness + auto-generates if blank.
  let slug: string | null = null;
  if (raw.slug) {
    if (!/^[a-z0-9-]+$/.test(raw.slug)) {
      errors.push('slug must be lowercase letters, digits, and dashes only');
    } else {
      slug = raw.slug;
    }
  }

  // URL fields. Empty is fine; non-empty must look like a URL.
  let heroPhotoUrl: string | null = null;
  if (raw.hero_photo_url) {
    try { new URL(raw.hero_photo_url); heroPhotoUrl = raw.hero_photo_url; }
    catch { errors.push('hero_photo_url is not a valid URL'); }
  }
  let videoEmbedUrl: string | null = null;
  if (raw.video_embed_url) {
    try { new URL(raw.video_embed_url); videoEmbedUrl = raw.video_embed_url; }
    catch { errors.push('video_embed_url is not a valid URL'); }
  }

  // Recurring template flag.
  const isTemplate = parseBool(raw.is_recurring_template);

  // Days JSON — multi-day per-day overrides.
  let days: EventDay[] | undefined = undefined;
  if (raw.days_json) {
    try {
      const parsed = JSON.parse(raw.days_json);
      if (!Array.isArray(parsed)) {
        errors.push('days_json must be a JSON array');
      } else {
        const days_: EventDay[] = [];
        for (const d of parsed) {
          if (typeof d?.day_index !== 'number' || typeof d?.day_date !== 'string') {
            errors.push('each day in days_json needs day_index (number) and day_date ("YYYY-MM-DD")');
            break;
          }
          if (!/^\d{4}-\d{2}-\d{2}$/.test(d.day_date)) {
            errors.push(`days_json: day_date "${d.day_date}" must be YYYY-MM-DD`);
            break;
          }
          days_.push({
            day_index: d.day_index,
            day_date: d.day_date,
            start_time: d.start_time ?? null,
            end_time: d.end_time ?? null,
            doors_open_text: d.doors_open_text ?? null,
            buffer_before_minutes: d.buffer_before_minutes ?? null,
            buffer_after_minutes: d.buffer_after_minutes ?? null,
            provisions: d.provisions ?? null,
            agenda_md: d.agenda_md ?? null,
            notes: d.notes ?? null,
          });
        }
        if (days_.length > 0) days = days_;
      }
    } catch (err: any) {
      errors.push('days_json is not valid JSON: ' + (err?.message || ''));
    }
  }

  if (errors.length > 0) {
    return { rowNumber, raw, errors, warnings };
  }

  const payload: EventCreateInput = {
    entry_title: title,
    entry_description: raw.description || null,
    start_date: startISO!,
    end_date: endISO!,
    audience,
    location_details: raw.location || null,
    venue_details: null, // bulk: venue_details lives in the location library
    resource_id: resourceId,
    event_type: raw.event_type || 'Workshop',
    max_attendees: maxAttendees,
    materials: raw.materials || null,
    cost_text: raw.cost_text || null,
    stipend_text: raw.stipend_text || null,
    doors_open_text: raw.doors_open_text || null,
    provisions,
    tag_ids: tagIds,
    // Round 1
    slug,
    // Round 2 (modal markdown)
    agenda_md: raw.agenda_md || null,
    about_md: raw.about_md || null,
    presenters_md: raw.presenters_md || null,
    // Round 2 (media)
    hero_photo_url: heroPhotoUrl,
    video_embed_url: videoEmbedUrl,
    // Round 2 (series + prereq)
    series_id: seriesId,
    sequence_number: sequenceNumber,
    instance_label: raw.instance_label || null,
    prereq_event_ids: prereqIds,
    prereq_text: raw.prereq_text || null,
    // Round 3 (template)
    is_recurring_template: isTemplate || undefined,
    instance_notes: raw.instance_notes || null,
    // Round 4: bulk-uploaded events ALWAYS land as drafts. The admin
    // reviews and Go-Lives each one from the detail page or events list.
    // This is a safety default — accidental publishing of dozens of half-
    // ready events isn't possible from a CSV.
    is_draft: true,
    // Round 2 (multi-day)
    days,
  };

  return { rowNumber, raw, payload, errors, warnings };
}

// ----- Component -----------------------------------------------------------

interface SubmitState {
  total: number;
  completed: number;
  succeeded: number;
  failed: number;
  perRow: Record<number, { status: 'pending' | 'ok' | 'failed'; message?: string }>;
  // Task #42: slug of the first successfully-created event, used by the
  // post-create "View first created event" button.
  firstSlug?: string;
}

export function CsvBulkUpload() {
  const { token } = useAuth();
  const [csvText, setCsvText] = useState('');
  const [tags, setTags] = useState<EventTag[]>([]);
  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [eventsList, setEventsList] = useState<PublicEvent[]>([]);
  const [resourceList, setResourceList] = useState<SharedResource[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string>('');
  const [submit, setSubmit] = useState<SubmitState | null>(null);
  // Round 4 Tier 2 (task #42): row-level controls.
  //   excludedRows — rowNumbers the admin opted out of (won't submit).
  //   expandedRow  — which row is currently being inline-edited (one at a time).
  //   edits        — per-row column overrides; merged into raw before validation
  //                  so the admin can fix a typo without re-pasting the CSV.
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<number, Record<string, string>>>({});

  useEffect(() => {
    api.tags().then((d) => setTags(d.tags ?? [])).catch(() => {});
    api.series().then((d) => setSeriesList(d.series ?? [])).catch(() => {});
    // includePast+includeTemplates so prereq lookups work for older events too.
    api.events({ includePast: true, includeTemplates: true, token: token ?? undefined })
      .then((d) => setEventsList(d.events ?? []))
      .catch(() => {});
    // Task #47: shared resource library for the resource_name CSV column.
    if (token) {
      api.sharedResources(token).then((d) => setResourceList(d.resources ?? [])).catch(() => {});
    }
  }, [token]);

  const lookups: LookupTables = useMemo(() => {
    const tagsByName = new Map<string, EventTag>();
    for (const t of tags) tagsByName.set(t.tag_name.toLowerCase(), t);

    const seriesByLabel = new Map<string, Series>();
    for (const s of seriesList) seriesByLabel.set(s.series_label.toLowerCase(), s);

    const eventBySlug = new Map<string, PublicEvent>();
    for (const e of eventsList) {
      if (e.slug) eventBySlug.set(e.slug, e);
    }
    const resourceByName = new Map<string, SharedResource>();
    for (const r of resourceList) resourceByName.set(r.resource_name.toLowerCase(), r);
    return { tagsByName, seriesByLabel, eventBySlug, resourceByName };
  }, [tags, seriesList, eventsList, resourceList]);

  // Re-parse whenever the text changes OR a lookup library finishes loading
  // OR an inline edit happens. Edits are merged into the trimmed raw before
  // validation so the per-row error/warning state stays in sync.
  useEffect(() => {
    if (!csvText.trim()) {
      setRows([]);
      setParseError('');
      return;
    }
    const result = Papa.parse<Record<string, string>>(csvText, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => h.trim().toLowerCase(),
    });
    if (result.errors.length > 0) {
      setParseError(result.errors.map((e) => `Row ${e.row}: ${e.message}`).join(' · '));
    } else {
      setParseError('');
    }
    const parsed = (result.data ?? []).map((raw, i) => {
      const rowNumber = i + 2;
      const trimmed = trimAll(raw);
      const edit = edits[rowNumber];
      const merged = edit ? { ...trimmed, ...edit } : trimmed;
      return validateRow(rowNumber, merged, lookups);
    });
    setRows(parsed);
  }, [csvText, lookups, edits]);

  // Effective counts — excludes opted-out rows from the "valid" tally so the
  // CTA reflects what will actually submit.
  const includedRows = rows.filter((r) => !excludedRows.has(r.rowNumber));
  const validCount = includedRows.filter((r) => r.errors.length === 0).length;
  const invalidCount = includedRows.length - validCount;
  const excludedCount = rows.length - includedRows.length;

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(String(e.target?.result ?? ''));
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csv = Papa.unparse({
      fields: [...TEMPLATE_HEADERS],
      data: TEMPLATE_SAMPLE_ROWS.map((row) => TEMPLATE_HEADERS.map((h) => row[h])),
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'uif-event-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Inline-edit helpers ----------------------------------------------------
  const updateField = (rowNumber: number, key: string, value: string) => {
    setEdits((prev) => ({
      ...prev,
      [rowNumber]: { ...(prev[rowNumber] ?? {}), [key]: value },
    }));
  };

  const toggleExclude = (rowNumber: number) => {
    setExcludedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber); else next.add(rowNumber);
      return next;
    });
  };

  const toggleExpand = (rowNumber: number) => {
    setExpandedRow((prev) => prev === rowNumber ? null : rowNumber);
  };

  const runSubmit = async () => {
    if (!token) return;
    // Only submit rows that are (a) not excluded and (b) validated successfully.
    const valid = rows.filter((r) => !excludedRows.has(r.rowNumber) && r.errors.length === 0 && r.payload);
    if (valid.length === 0) return;
    const state: SubmitState = {
      total: valid.length,
      completed: 0,
      succeeded: 0,
      failed: 0,
      perRow: Object.fromEntries(valid.map((r) => [r.rowNumber, { status: 'pending' as const }])),
    };
    setSubmit(state);
    // Also track which event slug succeeded first so we can offer "View first
    // created event" in the post-create card.
    const succeededSlugs: string[] = [];
    for (const row of valid) {
      try {
        await api.createEvent(token, row.payload!);
        state.succeeded++;
        state.perRow[row.rowNumber] = { status: 'ok' };
        if (row.payload?.slug) succeededSlugs.push(row.payload.slug);
      } catch (err: any) {
        state.failed++;
        state.perRow[row.rowNumber] = { status: 'failed', message: err?.message || 'Unknown error' };
      } finally {
        state.completed++;
        setSubmit({ ...state, perRow: { ...state.perRow }, firstSlug: succeededSlugs[0] });
      }
    }
  };

  // Reset everything — used by the "Add another batch" post-create button.
  const resetForAnother = () => {
    setCsvText('');
    setRows([]);
    setExcludedRows(new Set());
    setExpandedRow(null);
    setEdits({});
    setSubmit(null);
    setParseError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => { window.location.hash = '#admin/dashboard'; };

  const isSubmitting = submit != null && submit.completed < submit.total;
  const isDone = submit != null && submit.completed === submit.total;

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-uif-green-dark">Bulk event upload</h1>
            <p className="text-xs text-gray-500 -mt-0.5">CSV → many events at once</p>
          </div>
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
          <h2 className="text-sm font-semibold text-uif-green-dark uppercase tracking-wider">1. Start with the template</h2>
          <p className="text-sm text-gray-600">
            Download the template CSV, fill in one row per event, then upload or paste it below.
            Required columns: <code className="bg-gray-100 px-1 rounded">title</code>, <code className="bg-gray-100 px-1 rounded">start_date</code>, <code className="bg-gray-100 px-1 rounded">end_date</code>.
            Dates use the format <code className="bg-gray-100 px-1 rounded">YYYY-MM-DDTHH:MM</code>.
            Tags, provisions, and prereq slugs are comma-separated within a single cell.
          </p>
          <p className="text-xs text-gray-500">
            Multi-line fields (description, agenda_md, about_md, presenters_md, instance_notes) must
            be quoted in the CSV. Excel and Google Sheets handle this automatically when you save as CSV.
            For multi-day per-day overrides, use the <code className="bg-gray-100 px-1 rounded">days_json</code> column
            with a JSON array (see the second template row for an example).
          </p>

          <details className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
            <summary className="cursor-pointer font-medium text-gray-700">
              Excel users: enum columns for data-validation dropdowns
            </summary>
            <div className="mt-2 space-y-1.5">
              <div>
                <strong className="text-gray-800">audience</strong>: <code className="bg-white px-1 rounded">public</code>, <code className="bg-white px-1 rounded">amp</code>, <code className="bg-white px-1 rounded">agc</code>, <code className="bg-white px-1 rounded">internal</code>
              </div>
              <div>
                <strong className="text-gray-800">event_type</strong>: <code className="bg-white px-1 rounded">Workshop</code>, <code className="bg-white px-1 rounded">Webinar</code>, <code className="bg-white px-1 rounded">Field Day</code>, <code className="bg-white px-1 rounded">Conference</code>, <code className="bg-white px-1 rounded">Meeting</code>
                <span className="text-gray-500"> (free-form — these are the conventional values)</span>
              </div>
              <div>
                <strong className="text-gray-800">provisions</strong>: <code className="bg-white px-1 rounded">breakfast</code>, <code className="bg-white px-1 rounded">lunch</code>, <code className="bg-white px-1 rounded">dinner</code>, <code className="bg-white px-1 rounded">snacks</code> (comma-separate to combine, e.g. <code className="bg-white px-1 rounded">breakfast,lunch</code>)
              </div>
              <div>
                <strong className="text-gray-800">is_recurring_template</strong>: <code className="bg-white px-1 rounded">true</code>, <code className="bg-white px-1 rounded">false</code> (blank treated as false)
              </div>
              <div>
                <strong className="text-gray-800">tag_names</strong>: must match an existing tag in the Tags library (case-insensitive). Comma-separate multiples. Unknown names produce a warning and are skipped.
              </div>
              <div>
                <strong className="text-gray-800">series_label</strong>: must match an existing series label (case-insensitive). Unknown produces a warning.
              </div>
              <div>
                <strong className="text-gray-800">prereq_slugs</strong>: comma-separated slugs (URL fragments) of existing events. Each must match an existing event's slug exactly.
              </div>
              <div>
                <strong className="text-gray-800">resource_name</strong>: must match a shared resource (room) in the Locations / room library. If matched, the event lands on the AGC shared room calendar with the right booking status (Proposed when draft, Confirmed when Live). Unknown names produce a warning and the event is created without a room.
              </div>
            </div>
          </details>

          <p className="text-xs text-gray-500">
            <strong className="text-amber-700">Excel encoding tip:</strong> if your file has em dashes,
            curly quotes, or other special characters, save as <em>"CSV UTF-8 (Comma delimited) (*.csv)"</em>
            in Excel — not plain "CSV (Comma delimited)" — to keep them rendering correctly.
          </p>

          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <strong>Heads up:</strong> all bulk-uploaded events land as <strong>drafts</strong> — they
            are not visible on the public site until you Go Live on each. Open an event from the Events
            panel and click <strong>Go Live</strong> when you're ready to publish.
          </p>

          <button
            type="button"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 rounded-md border border-uif-green/30 bg-white px-3 py-1.5 text-sm font-medium text-uif-green-dark hover:bg-uif-green/5"
          >
            <Download className="h-4 w-4" />
            Download template (2 sample rows, all columns)
          </button>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
          <h2 className="text-sm font-semibold text-uif-green-dark uppercase tracking-wider">2. Upload or paste your CSV</h2>

          <label className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
            <Upload className="h-4 w-4" />
            Upload .csv file
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </label>

          <textarea
            rows={8}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder="…or paste CSV content here (header row + data rows)"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />

          {parseError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
              {parseError}
            </div>
          )}
        </section>

        {rows.length > 0 && (
          <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-uif-green-dark uppercase tracking-wider">3. Preview &amp; edit</h2>
              <div className="text-xs text-gray-600">
                <span className="text-uif-green-dark font-semibold">{validCount} valid</span>
                {invalidCount > 0 && <> · <span className="text-red-700 font-semibold">{invalidCount} blocked</span></>}
                {excludedCount > 0 && <> · <span className="text-gray-500">{excludedCount} excluded</span></>}
                {' · '}
                {rows.length} total
              </div>
            </div>

            <p className="text-[11px] text-gray-500">
              Untick <strong>Include</strong> to skip a row this batch. Click <strong>Edit</strong> on a
              row to fix any column inline — changes auto-validate as you type. Blocked rows must be
              fixed or excluded before submitting.
            </p>

            <div className="overflow-x-auto -mx-5">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Include</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Row</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Title</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Date</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Audience</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Extras</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Edit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((r) => {
                    const result = submit?.perRow[r.rowNumber];
                    const isExcluded = excludedRows.has(r.rowNumber);
                    const isExpanded = expandedRow === r.rowNumber;
                    const rowClass = isExcluded
                      ? 'opacity-50 bg-gray-50'
                      : r.errors.length > 0
                        ? 'bg-red-50'
                        : isExpanded
                          ? 'bg-uif-green/5'
                          : '';
                    return (
                      <Fragment key={r.rowNumber}>
                        <tr className={rowClass}>
                          <td className="px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={!isExcluded}
                              onChange={() => toggleExclude(r.rowNumber)}
                              aria-label={`Include row ${r.rowNumber}`}
                              className="h-4 w-4 accent-uif-green"
                            />
                          </td>
                          <td className="px-3 py-2 align-top text-gray-500">{r.rowNumber}</td>
                          <td className="px-3 py-2 align-top">
                            <div>{r.raw.title || <em className="text-gray-400">missing</em>}</div>
                            {r.raw.slug && <div className="text-[10px] text-gray-500">/{r.raw.slug}</div>}
                          </td>
                          <td className="px-3 py-2 align-top text-gray-700 whitespace-nowrap">{r.raw.start_date}</td>
                          <td className="px-3 py-2 align-top text-gray-700">{r.raw.audience || 'public'}</td>
                          <td className="px-3 py-2 align-top text-gray-700">
                            {extrasSummary(r.raw)}
                          </td>
                          <td className="px-3 py-2 align-top">
                            {isExcluded ? (
                              <span className="text-gray-500 italic">Excluded</span>
                            ) : r.errors.length > 0 ? (
                              <div className="text-red-700">
                                <div className="font-semibold inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Blocked</div>
                                {r.errors.map((e, i) => <div key={i}>· {e}</div>)}
                              </div>
                            ) : result?.status === 'ok' ? (
                              <span className="inline-flex items-center gap-1 text-uif-green-dark"><CheckCircle2 className="h-3 w-3" /> Created</span>
                            ) : result?.status === 'failed' ? (
                              <div className="text-red-700">
                                <div className="font-semibold inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Failed</div>
                                <div>{result.message}</div>
                              </div>
                            ) : result?.status === 'pending' && isSubmitting ? (
                              <span className="inline-flex items-center gap-1 text-gray-600"><Loader2 className="h-3 w-3 animate-spin" /> Pending</span>
                            ) : (
                              <span className="text-uif-green-dark">Valid</span>
                            )}
                            {r.warnings.map((w, i) => <div key={'w' + i} className="text-amber-700">⚠ {w}</div>)}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <button
                              type="button"
                              onClick={() => toggleExpand(r.rowNumber)}
                              disabled={isExcluded || isSubmitting}
                              className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              {isExpanded ? 'Done' : 'Edit'}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && !isExcluded && (
                          <tr className="bg-uif-green/5">
                            <td colSpan={8} className="px-5 py-4">
                              <RowEditor
                                rowNumber={r.rowNumber}
                                raw={r.raw}
                                edits={edits[r.rowNumber] ?? {}}
                                onChange={(k, v) => updateField(r.rowNumber, k, v)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Submit row OR post-create success card */}
            {isDone ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-700 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-green-900">
                    Done — created {submit!.succeeded} event{submit!.succeeded === 1 ? '' : 's'}
                    {submit!.failed > 0 && <span className="text-red-700"> (failed {submit!.failed})</span>}
                  </div>
                  <p className="text-xs text-green-800 mt-0.5">
                    All new events landed as <strong>drafts</strong>. Review them in the Events panel,
                    then Go Live when you're ready to publish.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {submit!.firstSlug && (
                      <a
                        href={`#e/${submit!.firstSlug}`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
                      >
                        <ArrowRight className="h-4 w-4" />
                        View first created event
                      </a>
                    )}
                    <a
                      href="#admin/events"
                      className="inline-flex items-center gap-1.5 rounded-md border border-green-700 bg-white px-3 py-1.5 text-sm font-semibold text-green-800 hover:bg-green-100"
                    >
                      Go to events list
                    </a>
                    <button
                      type="button"
                      onClick={resetForAnother}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add another batch
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {isSubmitting && (
                    <span className="inline-flex items-center gap-1"><Loader2 className="h-4 w-4 animate-spin" /> Submitting {submit!.completed} / {submit!.total}…</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={runSubmit}
                  disabled={isSubmitting || validCount === 0}
                  className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white px-4 py-2 text-sm font-semibold hover:bg-uif-green-dark disabled:bg-gray-300"
                >
                  <FileText className="h-4 w-4" />
                  Create {validCount} event{validCount === 1 ? '' : 's'}
                </button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

// Inline row editor. Renders one editable field per CSV column, grouped
// logically. Reads merged values (edits over raw); writes go through the
// onChange callback which the parent stores in its `edits` state and
// re-validates from. No internal state — fully controlled.
function RowEditor({ rowNumber, raw, edits, onChange }: {
  rowNumber: number;
  raw: Record<string, string>;
  edits: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  const val = (k: string): string => (edits[k] !== undefined ? edits[k] : (raw[k] ?? ''));
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(k, e.target.value);
  const setBool = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => onChange(k, e.target.checked ? 'true' : 'false');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-uif-green-dark uppercase tracking-wider">
          Row {rowNumber} · edit fields
        </div>
        <div className="text-[11px] text-gray-500 italic">
          Changes auto-validate. Tab to next field.
        </div>
      </div>

      {/* Basics */}
      <FieldGroup label="Basics">
        <Field label="Title *">
          <input type="text" value={val('title')} onChange={set('title')} className={fieldCls} />
        </Field>
        <Field label="Audience">
          <select value={val('audience') || 'public'} onChange={set('audience')} className={fieldCls}>
            <option value="public">public</option>
            <option value="amp">amp</option>
            <option value="agc">agc</option>
            <option value="internal">internal</option>
          </select>
        </Field>
        <Field label="Start date (YYYY-MM-DDTHH:MM) *">
          <input type="text" value={val('start_date')} onChange={set('start_date')} placeholder="2026-07-13T08:30" className={fieldCls} />
        </Field>
        <Field label="End date (YYYY-MM-DDTHH:MM) *">
          <input type="text" value={val('end_date')} onChange={set('end_date')} placeholder="2026-07-13T17:00" className={fieldCls} />
        </Field>
      </FieldGroup>

      {/* Where + capacity */}
      <FieldGroup label="Where &amp; capacity">
        <Field label="Location (free text)" wide>
          <input type="text" value={val('location')} onChange={set('location')} className={fieldCls} />
        </Field>
        <Field label="Resource (room) — must match library">
          <input type="text" value={val('resource_name')} onChange={set('resource_name')} placeholder="Up in Farms Training Center" className={fieldCls} />
        </Field>
        <Field label="Max attendees">
          <input type="text" inputMode="numeric" value={val('max_attendees')} onChange={set('max_attendees')} className={fieldCls} />
        </Field>
        <Field label="Event type">
          <input type="text" value={val('event_type')} onChange={set('event_type')} placeholder="Workshop" className={fieldCls} />
        </Field>
      </FieldGroup>

      {/* Cost / arrival / materials */}
      <FieldGroup label="Cost, arrival, materials">
        <Field label="Cost text">
          <input type="text" value={val('cost_text')} onChange={set('cost_text')} className={fieldCls} />
        </Field>
        <Field label="Stipend / honorarium text">
          <input type="text" value={val('stipend_text')} onChange={set('stipend_text')} className={fieldCls} />
        </Field>
        <Field label="Doors open / arrival info">
          <input type="text" value={val('doors_open_text')} onChange={set('doors_open_text')} className={fieldCls} />
        </Field>
        <Field label="What to bring (materials)">
          <input type="text" value={val('materials')} onChange={set('materials')} className={fieldCls} />
        </Field>
        <Field label="Provisions (comma-sep)" wide>
          <input type="text" value={val('provisions')} onChange={set('provisions')} placeholder="breakfast,lunch,dinner,snacks" className={fieldCls} />
        </Field>
      </FieldGroup>

      {/* URL + tags */}
      <FieldGroup label="URL &amp; tags">
        <Field label="Slug (lowercase + dashes)">
          <input type="text" value={val('slug')} onChange={set('slug')} placeholder="auto-generated if blank" className={fieldCls} />
        </Field>
        <Field label="Tag names (comma-sep)">
          <input type="text" value={val('tag_names')} onChange={set('tag_names')} placeholder="Food Safety, AMP" className={fieldCls} />
        </Field>
      </FieldGroup>

      {/* Long-form content */}
      <FieldGroup label="Description &amp; content">
        <Field label="Description" wide>
          <textarea rows={3} value={val('description')} onChange={set('description')} className={fieldCls + ' font-mono'} />
        </Field>
        <Field label="Agenda (markdown)" wide>
          <textarea rows={3} value={val('agenda_md')} onChange={set('agenda_md')} className={fieldCls + ' font-mono'} />
        </Field>
        <Field label="About (markdown)" wide>
          <textarea rows={3} value={val('about_md')} onChange={set('about_md')} className={fieldCls + ' font-mono'} />
        </Field>
        <Field label="Presenters (markdown)" wide>
          <textarea rows={3} value={val('presenters_md')} onChange={set('presenters_md')} className={fieldCls + ' font-mono'} />
        </Field>
      </FieldGroup>

      {/* Media */}
      <FieldGroup label="Media">
        <Field label="Hero photo URL" wide>
          <input type="text" value={val('hero_photo_url')} onChange={set('hero_photo_url')} placeholder="https://events.upinfarms.com/uploads/events/..." className={fieldCls} />
        </Field>
        <Field label="Video embed URL" wide>
          <input type="text" value={val('video_embed_url')} onChange={set('video_embed_url')} placeholder="https://www.youtube.com/embed/..." className={fieldCls} />
        </Field>
      </FieldGroup>

      {/* Series + prereqs */}
      <FieldGroup label="Series, sequence, prerequisites">
        <Field label="Series label (must match library)">
          <input type="text" value={val('series_label')} onChange={set('series_label')} className={fieldCls} />
        </Field>
        <Field label="Sequence number">
          <input type="text" inputMode="numeric" value={val('sequence_number')} onChange={set('sequence_number')} className={fieldCls} />
        </Field>
        <Field label="Instance label">
          <input type="text" value={val('instance_label')} onChange={set('instance_label')} className={fieldCls} />
        </Field>
        <Field label="Prereq slugs (comma-sep)">
          <input type="text" value={val('prereq_slugs')} onChange={set('prereq_slugs')} className={fieldCls} />
        </Field>
        <Field label="Prereq text (free form)" wide>
          <input type="text" value={val('prereq_text')} onChange={set('prereq_text')} className={fieldCls} />
        </Field>
      </FieldGroup>

      {/* Template + multi-day */}
      <FieldGroup label="Template &amp; multi-day">
        <Field label="Recurring template?">
          <label className="inline-flex items-center gap-2 text-xs">
            <input type="checkbox" checked={val('is_recurring_template').toLowerCase() === 'true'} onChange={setBool('is_recurring_template')} className="h-4 w-4 accent-uif-green" />
            yes
          </label>
        </Field>
        <Field label="Instance notes" wide>
          <textarea rows={2} value={val('instance_notes')} onChange={set('instance_notes')} className={fieldCls + ' font-mono'} />
        </Field>
        <Field label="days_json (per-day overrides; JSON array)" wide>
          <textarea rows={3} value={val('days_json')} onChange={set('days_json')} className={fieldCls + ' font-mono'} placeholder='[{"day_index":1,"day_date":"2026-07-13",...}]' />
        </Field>
      </FieldGroup>
    </div>
  );
}

// Shared input/textarea/select className so every field looks the same.
const fieldCls = 'w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none';

function FieldGroup({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 rounded-md bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">{label}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2">
        {children}
      </div>
    </div>
  );
}

function Field({ label, wide = false, children }: { label: React.ReactNode; wide?: boolean; children: React.ReactNode }) {
  return (
    <label className={'block ' + (wide ? 'md:col-span-2' : '')}>
      <span className="block text-[11px] text-gray-600 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

// Short summary of the optional/extras columns for the preview table, so the
// admin can spot at a glance whether a row's series + template + multi-day
// linkages came through right without scrolling 30 columns.
function extrasSummary(raw: Record<string, string>): React.ReactNode {
  const bits: string[] = [];
  if (raw.resource_name) bits.push(`room: ${raw.resource_name}`);
  if (raw.series_label) bits.push(`series: ${raw.series_label}`);
  if (raw.tag_names) bits.push(`tags: ${raw.tag_names}`);
  if (raw.is_recurring_template && raw.is_recurring_template.toLowerCase() !== 'false') bits.push('template');
  if (raw.days_json) bits.push('multi-day');
  if (raw.prereq_slugs) bits.push(`prereqs: ${raw.prereq_slugs.split(',').length}`);
  if (bits.length === 0) return <span className="text-gray-400">—</span>;
  return <div className="space-y-0.5">{bits.map((b, i) => <div key={i}>{b}</div>)}</div>;
}
